import { useQuery } from '@tanstack/react-query';
import dayjs from 'dayjs';
import Image from 'next/image';
import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import toast from 'react-hot-toast';
import { getCollabBlockStakingEntry } from '../../firebase-client';
import { useAlgo } from '../../hooks/useAlgo';
import Alert from '../Alert';
import Button from '../Button';
import Countdown from '../Countdown';
import Form from '../Form';
import FullScreenLoader from '../FullScreenLoader';
import Spinner from '../Spinner';
import CollabBlockStakingFormSelector from './CollabBlockStakingFormSelector';
import formatNumber from '../../utils/formatNumber';

const CollabBlockStakingForm = ({ collab }) => {
  const { address } = useAlgo();
  const { optInForBlockStaking } = useAlgo();
  const [loading, setLoading] = useState(false);

  const opensOn = dayjs(collab.stakingOpensOn);
  const lockedOn = dayjs(collab.stakingOpensOn).add(3, 'day');
  const overOn = dayjs(collab.stakingOpensOn).add(6, 'day');
  const stakingIsOpen = dayjs().isAfter(opensOn);
  const stakingIsLocked = dayjs().isAfter(lockedOn);
  const stakingIsOver = dayjs().isAfter(overOn);

  const form = useForm({
    defaultValues: {
      collab: collab.id,
      staked: {
        city: '',
        kitty: '',
        collab: '',
      },
    },
  });

  const stakingInfoQuery = useQuery({
    enabled: typeof collab.id === 'number' && typeof address === 'string',
    queryKey: ['stakingInfo', collab.id],
    queryFn: () => getCollabBlockStakingEntry(collab.id, address),
    onSuccess: (data) => {
      if (data) {
        form.setValue('staked', data.staked);
      }
    },
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    refetchIntervalInBackground: false,
  });

  const successfulStaking = stakingIsOver && !!stakingInfoQuery.data?.staked && !stakingInfoQuery.data?.disqualified;

  const handleSubmit = async (values) => {
    if (!stakingIsOpen) {
      toast.error('Staking is not open yet');
      return;
    }

    if (!values.staked.city || !values.staked.kitty || !values.staked.collab) {
      toast.error('Must select a city, kitty, and collab');
      return;
    }

    try {
      setLoading(true);
      const txn = await optInForBlockStaking(values);
      const response = await fetch('/api/blocks/stake', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: process.env.NEXT_PUBLIC_API_KEY,
        },
        body: JSON.stringify({ txn }),
      });
      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.message);
      }
      stakingInfoQuery.refetch();
      toast.success('Assets staked successfully');
    } catch (error) {
      toast.error(error.message || 'Something went wrong');
    } finally {
      setLoading(false);
    }
  };

  if (stakingInfoQuery.isLoading) {
    return (
      <div className='flex justify-center'>
        <Spinner />
      </div>
    );
  }

  return (
    <div className='flex flex-col gap-8'>
      <FullScreenLoader loading={loading} label='Staking NFTs...' />

      <div className='flex flex-col gap-4'>
        {stakingIsOpen && !stakingIsLocked && (
          <div className='text-center flex flex-col gap-2'>
            <p className='font-medium text-gray-400 text-xl'>Staking is now open for</p>
            <p className='text-gray-50 font-bold text-2xl'>
              <Countdown date={lockedOn.toDate()} simple />
            </p>
          </div>
        )}
      </div>

      <div className='bg-gray-900 rounded-lg p-8 flex flex-col gap-10 relative'>
        {stakingInfoQuery.data?.disqualified ? (
          <OverlayInfo>
            <p className='font-bold text-red-400 text-3xl'>Your staking entry has been disqualified.</p>
          </OverlayInfo>
        ) : (
          <>
            {stakingIsOver && (
              <OverlayInfo>
                <p className='font-bold text-gray-400 text-3xl'>Staking has ended. {successfulStaking && 'Congratulations!'}</p>
                {successfulStaking ? (
                  <p className='text-xl text-gray-50'>
                    The <strong className='font-bold text-gray-50'>{collab.blockName}</strong> will be airdropped to your wallet within a few days.
                  </p>
                ) : (
                  <p className='text-xl text-gray-50'>Sorry but you missed this one. Check back later for more Collab Blocks!</p>
                )}
              </OverlayInfo>
            )}

            {!!stakingInfoQuery.data && !stakingIsLocked && (
              <OverlayInfo>
                <p className='font-medium text-gray-400 text-2xl'>You have successfully staked your NFTs!</p>
                <p className='text-xl text-gray-50'>Sit back and relax!</p>
              </OverlayInfo>
            )}
            {!stakingIsOpen && (
              <OverlayInfo>
                <p className='font-medium text-gray-400 text-xl'>Staking opens in</p>
                <p className='text-gray-50 font-bold text-2xl'>
                  <Countdown date={opensOn.toDate()} simple />
                </p>
              </OverlayInfo>
            )}
            {stakingIsLocked && !stakingIsOver && (
              <OverlayInfo>
                <p className='font-medium text-gray-400 text-xl'>Staking ends in</p>
                <p className='text-gray-50 font-bold text-2xl'>
                  <Countdown date={overOn.toDate()} simple />
                </p>
                {!stakingInfoQuery.data?.staked && stakingIsLocked && (
                  <div className='mt-5'>
                    <Alert type='danger'>Sorry but you missed the staking window. Check back later for more Collab Blocks!</Alert>
                  </div>
                )}
              </OverlayInfo>
            )}
          </>
        )}

        <div>
          <h2 className='text-gray-400 text-xl text-center relative z-10'>
            Stake your <strong className='text-gray-50 font-bold'>{collab.collection}</strong> for a{' '}
            <strong className='text-gray-50 font-bold'>{collab.blockName}</strong>
          </h2>
          {collab.isToken && (
            <p className='text-gray-500 text-center mt-4'>
              Requires {formatNumber(collab.requiredAssetAmount / 10 ** 6)} {collab.collection}
            </p>
          )}
        </div>
        <div className='relative z-10'>
          <Form form={form} onSubmit={handleSubmit}>
            <div className='flex flex-col justify-center items-center'>
              <div className='max-w-[250px] mx-auto mb-8'>
                <h2 className='text-2xl font-bold text-gray-600 mb-5 text-center'>Reward</h2>
                <div className='relative aspect-square'>
                  <Image src={collab.blockImage} alt={collab.blockName} width={500} height={500} className='rounded-md' />
                </div>
                <div className='flex flex-col gap-0.5 text items-center justify-center text-center'>
                  <strong className='text-gray-50 text-xl font-bold'>{collab.blockName}</strong>
                  <span className='text-gray-400 text-sm'>{collab.blockId}</span>
                </div>
              </div>

              <h2 className='text-2xl font-bold text-gray-600 mb-5 text-center'>Stakers</h2>

              <div className={'flex flex-col md:flex-row gap-5 mx-auto w-full max-w-4xl justify-center items-center'}>
                <CollabBlockStakingFormSelector type='city' label='Shitty City' collab={collab} />
                <CollabBlockStakingFormSelector type='kitty' label='Shitty Kitty' collab={collab} />
                <CollabBlockStakingFormSelector type='collab' label={collab.collection} collab={collab} />
              </div>
            </div>

            {stakingIsOpen && !stakingIsLocked && !stakingInfoQuery.data && (
              <div className='mt-8 flex justify-center'>
                <Button loading={loading} type='submit'>
                  Stake Assets
                </Button>
              </div>
            )}
          </Form>
        </div>
      </div>
    </div>
  );
};

const OverlayInfo = ({ children }) => {
  return (
    <div className='absolute inset-0 w-full h-full bg-gray-900/[0.80] flex items-center justify-center z-20 rounded-lg p-8'>
      <div className='text-center flex flex-col gap-2'>{children}</div>
    </div>
  );
};

export default CollabBlockStakingForm;
